import { useCallback, useMemo, useState } from 'react';
import { useStore } from 'zustand';
import type { ChapterId } from '@/core/content/ids';
import type { Game } from '@/game/Game';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useBack } from '@/ui/back/backStack';
import { useUiImage } from '@/ui/art/uiArt';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import { DialogueOverlay } from './DialogueOverlay';
import styles from './UnlockModal.module.css';

/**
 * Granny's Story (story-dialogue §3): a newly unlocked chapter with `autoPrompt` comes up once the player is
 * free (no mini game, no drag, no sheet open). Chapters opened by progress first ask "Chương mới" [Để sau]
 * [Đọc ngay]; "Để sau" leaves it for the Album (and asks again next visit). The prologue and Granny's letter
 * in the tutorial play straight away, and so does a chapter told by a postcard once that card is opened.
 * Reading or skipping marks it seen; it stays in the Album to re-read. Never blocks rewards.
 */
export function StoryHost({ game, blocked }: { game: Game; blocked: boolean }) {
  const { content, dispatch } = useServices();
  const unlocked = useSave((s) => s.story.unlockedChapters);
  const seen = useSave((s) => s.story.seenChapters);
  const cards = useSave((s) => s.story.postcards.map((p) => p.postcardId).join(','));
  const [later, setLater] = useState<readonly ChapterId[]>([]);
  const [reading, setReading] = useState<ChapterId | null>(null);
  const miniGame = useStore(game.miniGameUi, (s) => s.run !== null);
  const dragging = useStore(game.placementUi, (s) => s.dragging !== null);

  const pending = useMemo(() => {
    const received = cards.split(',');
    const open = unlocked
      .filter((id) => !seen.includes(id) && !later.includes(id))
      .map((id) => content.chapters.get(id))
      .filter(
        (c) =>
          c?.autoPrompt === true ||
          (c?.playsWithPostcard !== undefined && received.includes(c.playsWithPostcard)),
      )
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
    return open[0];
  }, [unlocked, seen, later, cards, content]);

  const onDone = useCallback(
    (chapterId: ChapterId, skipped: boolean) => {
      dispatch({ type: 'markChapterSeen', chapterId, skipped });
    },
    [dispatch],
  );

  if (!pending || blocked || miniGame || dragging) return null;
  const ask =
    pending.unlockedBy.kind === 'unlock' && pending.playsWithPostcard === undefined && reading !== pending.id;
  if (ask)
    return (
      <NewChapter
        chapterId={pending.id}
        titleKey={pending.titleKey}
        onRead={() => {
          setReading(pending.id);
        }}
        onLater={() => {
          setLater((ids) => [...ids, pending.id]);
        }}
      />
    );
  return (
    <DialogueOverlay
      key={pending.id}
      dialogueId={pending.dialogueId}
      onDone={(skipped) => {
        onDone(pending.id, skipped);
      }}
    />
  );
}

/** "Chương mới" (story-dialogue §3): a small card before the chapter, so it never ambushes the player. */
function NewChapter({
  chapterId,
  titleKey,
  onRead,
  onLater,
}: {
  chapterId: ChapterId;
  titleKey: string;
  onRead: () => void;
  onLater: () => void;
}) {
  const { t } = useI18n();
  const art = useUiImage(`story_${chapterId}`);
  useBack(onLater);
  return (
    <div className={styles.scrim}>
      <section
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-chapter-title"
        data-testid="chapter-popup"
      >
        <p className={styles.opened}>{t('ui.story.new_chapter')}</p>
        <h2 id="new-chapter-title" className={styles.title}>
          {t(titleKey)}
        </h2>
        {art && <img src={art} alt="" className={styles.chapterArt} draggable={false} />}
        <p className={styles.next}>{t('story.ui.new_chapter')}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={onLater}>
            {t('story.ui.skip')}
          </button>
          <button type="button" className={styles.primary} data-testid="chapter-read" onClick={onRead}>
            {t('ui.story.read_now')}
          </button>
        </div>
      </section>
    </div>
  );
}
